/**
 * Description du fichier:
 * Service principal pour la gestion des devis, le calcul des prix et l'optimisation du calepinage.
 * 
 * Relations avec d'autres fichiers:
 * - Utilise les types définis dans ../types/dimensions.ts et ./types.ts
 * - Interagit avec DimensionsService pour le calcul des prix des articles
 * - Interagit avec CalepinageService pour l'optimisation de l'utilisation des matériaux
 * - Utilise EntrepriseService pour récupérer les informations fiscales de l'entreprise
 * - Utilise les fonctions d'adaptation de DevisAdapter pour convertir entre différents formats de devis
 * 
 * Actions déclenchées:
 * - Calcule le prix des articles individuels en tenant compte de leurs caractéristiques
 * - Calcule le prix total d'un devis avec optimisation du calepinage
 * - Gère la conversion des prix TTC en HT en fonction du statut TVA de l'entreprise
 * - Regroupe les articles par matière et couleur pour optimiser l'utilisation des matériaux
 * - Calcule les économies réalisées grâce à l'optimisation du calepinage
 */

import { Article, Devis, GroupeMatiere } from '../types/dimensions';
import { Devis as DevisType } from './types';
import { DimensionsService } from './DimensionsService';
import { CalepinageService } from './CalepinageService';
import { Matiere } from '../pages/Devis/ConfigurateurArticle/types';
import { entrepriseService } from './EntrepriseService';
import { convertToDevisDimensions, convertToDevisService } from '../utils/DevisAdapter';
import { subscriptionService } from './SubscriptionService';
import { apiFetch } from './httpClient';

// Taux de TVA par défaut (20%)
const TAUX_TVA_DEFAUT = 20;

// Taux horaire par défaut (30€/h)
const TAUX_HORAIRE_DEFAUT = 30;

export class DevisService {
  /**
   * Convertit un prix TTC en HT en fonction du taux de TVA
   */
  private static convertirTTCversHT(prixTTC: number, tauxTVA: number): number {
    return prixTTC / (1 + tauxTVA / 100);
  }
  
  /**
   * Calcule le prix d'un article
   */
  static calculerPrixArticle(article: Article, matieres: Matiere[]): Article {
    // Si l'article a déjà un prix unitaire et des détails de prix calculé, ne pas recalculer
    if (article.prixUnitaire && article.prixUnitaire > 0 && (article as any).prixCalcule) {
      return {
        ...article,
        prix: article.prixUnitaire,
        prixUnitaire: article.prixUnitaire, // S'assurer que prixUnitaire est bien préservé
        couts: (article as any).prixCalcule.details?.couts || {}
      };
    }
    
    // Vérifier si l'utilisateur a un abonnement Premium
    const isPremium = subscriptionService.hasPremiumFeatures();
    
    // Calculer le prix de l'article en tenant compte du type d'abonnement
    const resultat = DimensionsService.calculerPrixArticle(article, matieres, TAUX_HORAIRE_DEFAUT, isPremium);
    
    // Mettre à jour l'article avec le prix calculé
    return {
      ...article,
      prix: resultat.prix,
      prixUnitaire: resultat.prix, // S'assurer que prixUnitaire est synchronisé avec prix
      couts: resultat.details.couts
    };
  }
  
  /**
   * Calcule le prix total du devis avec optimisation du calepinage
   * Cette méthode accepte un devis au format types/dimensions.ts ou services/types.ts
   */
  static async calculerPrixDevis(devis: Devis | DevisType, matieres: Matiere[]): Promise<Devis> {
    // Vérifier si l'utilisateur a un abonnement Premium
    const isPremium = subscriptionService.hasPremiumFeatures();
    // Convertir le devis au format types/dimensions.ts si nécessaire
    const devisDimensions = 'groupesMatiere' in devis ? devis as Devis : convertToDevisDimensions(devis as DevisType);
    
    // Si l'utilisateur n'est pas Premium, calculer le prix sans optimisation
    if (!isPremium) {
      // Calculer le prix de chaque article
      const articlesAvecPrix = devisDimensions.articles.map(article => {
        // Vérifier si c'est un article libre
        if (article.estArticleLibre) {
          // Pour les articles libres, on utilise directement le prix unitaire
          return article;
        } else {
          // Pour les articles normaux, calculer le prix
          return this.calculerPrixArticle(article, matieres);
        }
      });
      
      // Calculer le montant total sans optimisation
      const montantTotal = articlesAvecPrix.reduce(
        (total, article) => total + (article.prixUnitaire || 0) * article.quantite, 0
      );
      
      // Mettre à jour le devis avec les nouvelles valeurs
      return {
        ...devisDimensions,
        articles: articlesAvecPrix,
        montantTotal
      };
    }
    
    // Pour les utilisateurs Premium, utiliser l'endpoint backend
    try {
      // Vérifier que tous les articles ont les informations nécessaires
      for (const article of devisDimensions.articles) {
        if (!article.id) {
          throw new Error(`L'article "${article.nom}" n'a pas d'ID valide`);
        }
        if (!article.prixUnitaire) {
          throw new Error(`L'article "${article.nom}" n'a pas de prix unitaire valide`);
        }
      }

      // Appeler l'endpoint backend avec apiFetch
      const devisOptimise = await apiFetch<Devis>('/devis/optimiser', {
        method: 'POST',
        body: JSON.stringify({
          ...devisDimensions
        })
      });
      
      // Vérifier que l'économie est bien présente
      if (devisOptimise.economieTotal === undefined) {
        throw new Error("L'économie n'a pas été calculée par le backend. Vérifiez l'implémentation du backend.");
      }
      
      
      return devisOptimise;
    } catch (error) {
      console.error("Erreur lors de l'optimisation du devis:", error);
      throw error; // Propager l'erreur pour qu'elle soit gérée par l'appelant
    }
  }
}